/**
 * Schermo acceso durante il turno (sezione 5.7, punto 3).
 *
 * Serve a una cosa concreta: su un telefono lo schermo si spegne dopo trenta
 * secondi, e con lo schermo spento la scheda viene sospesa — il suono non
 * parte e l'interrogazione periodica rallenta o si ferma. Chi è in turno con la
 * console aperta si accorgerebbe della chiamata quando è già passata a un
 * altro.
 *
 * Si prende **solo mentre il turno è in corso** e si rilascia appena finisce:
 * tenere acceso lo schermo di qualcuno che non è in ascolto è consumo di
 * batteria preso senza motivo.
 *
 * Il browser lo revoca da sé quando la scheda va in secondo piano, e non lo
 * restituisce da solo: per questo si riprende al ritorno.
 */

type WakeLockSentinelLike = { released: boolean; release: () => Promise<void> };

type WakeLockNavigator = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
};

let sentinel: WakeLockSentinelLike | undefined;
/** L'acquisizione in volo: due richieste insieme non chiedono due blocchi. */
let inFlight: Promise<boolean> | undefined;
let wanted = false;
let listening = false;

export function wakeLockSupported(): boolean {
  return typeof (globalThis.navigator as WakeLockNavigator)?.wakeLock?.request === 'function';
}

export function wakeLockHeld(): boolean {
  return sentinel !== undefined && !sentinel.released;
}

/**
 * Chiede di tenere acceso lo schermo. Non solleva: un browser che dice di no —
 * batteria scarica, permesso negato, scheda in secondo piano — non è un errore
 * da mostrare a chi sta entrando in turno.
 */
export async function requestWakeLock(): Promise<boolean> {
  wanted = true;
  listenForReturn();
  return acquire();
}

export async function releaseWakeLock(): Promise<void> {
  wanted = false;
  // Si aspetta anche un'acquisizione in volo: senza, il blocco preso un
  // istante dopo resterebbe attivo con nessuno che sa di averlo.
  await inFlight?.catch(() => undefined);
  const current = sentinel;
  sentinel = undefined;
  try {
    await current?.release();
  } catch {
    // Già rilasciato dal browser: è lo stato che volevamo.
  }
}

/**
 * Prende il blocco, una volta sola.
 *
 * Due chiamate si sovrappongono davvero: l'inizio del turno e il ritorno in
 * primo piano. Senza la promessa condivisa, entrambe superavano il controllo —
 * `wakeLockHeld()` è falso finché il primo `await` non è tornato — ed entrambe
 * chiedevano un blocco: **la prima sentinella veniva sostituita senza essere
 * rilasciata**, cioè lo schermo restava acceso per sempre, che è la batteria
 * che questo file esiste per non prendere.
 */
async function acquire(): Promise<boolean> {
  if (!wanted || wakeLockHeld()) return wakeLockHeld();
  const wakeLock = (globalThis.navigator as WakeLockNavigator)?.wakeLock;
  if (!wakeLock) return false;

  inFlight ??= (async () => {
    try {
      const taken = await wakeLock.request('screen');
      // Nel frattempo il turno può essere finito: un blocco preso dopo il
      // rilascio non lo rilascerebbe più nessuno.
      if (!wanted) {
        await taken.release().catch(() => undefined);
        return false;
      }
      sentinel = taken;
      return true;
    } catch {
      return false;
    } finally {
      inFlight = undefined;
    }
  })();

  return inFlight;
}

/**
 * Al ritorno in primo piano si riprende il blocco.
 *
 * Un solo ascoltatore per pagina, aggiunto alla prima richiesta: la console
 * entra e esce dal turno più volte in una serata, e un ascoltatore per volta
 * sarebbe una perdita lenta.
 */
function listenForReturn(): void {
  if (listening || typeof globalThis.document === 'undefined') return;
  listening = true;
  globalThis.document.addEventListener('visibilitychange', () => {
    if (globalThis.document.visibilityState === 'visible') void acquire();
  });
}

/** Solo per i test. */
export function resetWakeLockForTests(): void {
  sentinel = undefined;
  inFlight = undefined;
  wanted = false;
}
